import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, ROLE_LABEL } from '../lib/api';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { useStore } from '../store/StoreProvider';
import { waitingSessions } from '../store/selectors';
import { NavGroup, NavItem } from './NavGroup';
import { NAV_GROUPS, NAV_TOP, groupOf, isActive, readOpen, saveOpen } from './nav';

// Il menu a gruppi richiudibili (tela, artboard 39): Dashboard e Pops in alto, poi Lavoro e
// Risorse; in fondo Impostazioni. Le notifiche stanno nella campanella dell'intestazione. Nessuna
// voce porta a una vista senza dati veri.
export function Sidebar({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { state, dispatch } = useStore();
  const nav = useNavigate();
  const loc = useLocation();
  const waiting = waitingSessions(state).length;
  const width = collapsed ? 64 : 256;
  const [open, setOpen] = useState<Record<string, boolean>>(readOpen);

  // il gruppo della pagina aperta si apre da solo; gli altri restano come li si è lasciati
  const current = groupOf(loc.pathname);
  useEffect(() => {
    if (!current) return;
    setOpen((o) => {
      if (o[current]) return o;
      const next = { ...o, [current]: true };
      saveOpen(next);
      return next;
    });
  }, [current]);
  const toggle = (id: string) =>
    setOpen((o) => {
      const next = { ...o, [id]: !o[id] };
      saveOpen(next);
      return next;
    });

  return (
    <nav
      aria-label="Menu"
      style={{
        width,
        flex: 'none',
        background: 'var(--panel)',
        borderRight: '1px solid var(--border)',
        padding: '12px 8px',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 56,
        height: 'calc(100vh / var(--zoom) - 56px)',
        overflowY: 'auto',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_TOP.map((n) => (
          <NavItem
            key={n.to}
            {...n}
            pathname={loc.pathname}
            collapsed={collapsed}
            badge={n.to === '/' ? waiting : undefined}
            onNavigate={onNavigate}
          />
        ))}
        <span
          aria-hidden
          style={{
            height: collapsed ? 1 : 8,
            margin: collapsed ? '6px 10px' : 0,
            background: collapsed ? 'var(--border)' : 'none',
          }}
        />
        {NAV_GROUPS.map((g) => (
          <NavGroup
            key={g.id}
            def={g}
            open={!!open[g.id]}
            onToggle={() => toggle(g.id)}
            pathname={loc.pathname}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>
      <span style={{ flex: 1 }} />
      {/* Impostazioni e Profili sono dell'amministratore (poptown.api.roles) */}
      {state.role === 'admin' && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
          <NavItem
            to="/settings"
            label="Impostazioni"
            icon="settings"
            tint="sun"
            pathname={loc.pathname}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </div>
      )}

      {/* §3.2.1 — identità e configurazione sono la stessa famiglia di cose, e l'angolo in
          alto a destra va lasciato a ciò che cambia da solo e va guardato. */}
      <UserBlock
        collapsed={collapsed}
        name={state.display || state.user}
        role={ROLE_LABEL[state.role]}
        avatar={state.avatar}
        active={loc.pathname === '/profilo'}
        waiting={waiting}
        onProfile={() => {
          onNavigate?.();
          nav('/profilo');
        }}
        onLogout={() => {
          // prima il server chiude la sessione, poi la plancia torna al login
          void api
            .logout()
            .catch(() => undefined)
            .finally(() => dispatch({ type: 'auth/logout' }));
        }}
      />
    </nav>
  );
}

export function BottomNav() {
  const { state } = useStore();
  const loc = useLocation();
  const waiting = waitingSessions(state).length;
  const items = [
    { to: '/', label: 'Dashboard', icon: 'layout-dashboard' },
    { to: '/progetti', label: 'Progetti', icon: 'folder' },
    { to: '/pianifica', label: 'Pianifica', icon: 'calendar-clock' },
    state.role === 'admin'
      ? { to: '/settings', label: 'Impost.', icon: 'settings' }
      : { to: '/profilo', label: 'Profilo', icon: 'user' },
  ];
  return (
    <nav
      style={{
        position: 'sticky',
        bottom: 0,
        zIndex: 50,
        flex: 'none',
        background: 'color-mix(in srgb, var(--panel) 94%, transparent)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {items.map((n) => {
        const active = isActive(n.to, loc.pathname);
        return (
          <NavLink
            key={n.to}
            to={n.to}
            style={{
              flex: 1,
              minHeight: 52,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              textDecoration: 'none',
              position: 'relative',
              color: active ? 'var(--accent)' : 'var(--dim)',
            }}
          >
            <Icon name={n.icon} size={20} />
            <span style={{ font: '500 10px/12px var(--font-ui)' }}>{n.label}</span>
            {n.to === '/' && waiting > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  right: '50%',
                  marginRight: -18,
                  height: 16,
                  minWidth: 16,
                  padding: '0 4px',
                  borderRadius: 'var(--r-full)',
                  background: 'var(--warn)',
                  color: 'var(--badge-ink)',
                  font: '600 10px/16px var(--font-ui)',
                  textAlign: 'center',
                }}
              >
                {waiting}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserBlock({
  collapsed,
  name,
  role,
  avatar,
  active,
  waiting,
  onProfile,
  onLogout,
}: {
  collapsed: boolean;
  name: string;
  role: string;
  avatar: string | null;
  active: boolean;
  waiting: number;
  onProfile: () => void;
  onLogout: () => void;
}) {
  const exit = () => {
    // Conferma SOLO se ci sono sessioni che aspettano una risposta, e dicendo quante
    if (waiting > 0) {
      const plural = waiting === 1 ? 'sessione aspetta' : 'sessioni aspettano';
      if (!window.confirm(`${waiting} ${plural} una tua risposta. Esci comunque?`)) return;
    }
    onLogout();
  };

  const face = <UserAvatar avatar={avatar} name={name} size={36} />;

  if (collapsed) {
    return (
      <div
        title={`${name} · Profilo`}
        style={{ marginTop: 8, display: 'grid', placeItems: 'center' }}
      >
        <button
          onClick={onProfile}
          aria-label="Profilo"
          style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
        >
          {face}
        </button>
      </div>
    );
  }

  // il clic sul nome apre la pagina profilo (avatar, nome, password, passkey)
  return (
    <div className={`user-block${active ? ' is-active' : ''}`}>
      <button type="button" onClick={onProfile} className="user-block-link">
        {face}
        <span style={{ flex: 1, minWidth: 0, display: 'grid' }}>
          <span className="t-strong user-block-name">{name}</span>
          <span className="t-caption faint">{role}</span>
        </span>
      </button>
      <Button variant="icon" icon="log-out" aria-label="Esci" onClick={exit} />
    </div>
  );
}

/** L'avatar di una persona: la sua immagine o un pop, altrimenti l'iniziale del nome. */
export function UserAvatar({
  avatar,
  name,
  size,
}: {
  avatar: string | null;
  name: string;
  size: number;
}) {
  if (avatar)
    return (
      <img
        src={avatar}
        alt=""
        width={size}
        height={size}
        className="user-avatar"
        style={{ width: size, height: size }}
      />
    );
  return (
    <span
      aria-hidden="true"
      className="user-avatar user-avatar-initial"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
    >
      {(name.trim()[0] ?? '?').toUpperCase()}
    </span>
  );
}
